'use client';

import { useStatusBattery } from 'hooks/statekeeping';
import { BatteryState_PowerEnum } from 'lib/generated/proto/sensor_msgs/msg/BatteryState';
import { Status_Battery_Status } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Status';

import { useRobotBattery } from 'lib/ros-abstraction/telemetry/robot-state/hooks';
import { useRobotIdentity } from './useRobotIdentity';

export type RobotBatteryInfo = {
  /** 0–100 */
  percentage: number;
  isCharging: boolean;
  powerStatus: BatteryState_PowerEnum;
  /** Voltage in Volts — undefined when not available */
  voltage?: number;
  /** Temperature in °C — undefined when not available */
  temperature?: number;
};

/**
 * Unified battery hook — returns normalized battery info regardless of robot type.
 * - ROS-abstraction robots: reads `$.battery` from `${deviceName}:Reported:State` (sensor_msgs/BatteryState)
 * - Legacy robots (Spot, DJI): reads from `${deviceName}:Reported:Status` -> $.battery
 */
export function useBattery(): { battery: RobotBatteryInfo } {
  const { usesRosAbstraction } = useRobotIdentity();

  // Both hooks must always be called (React rules of hooks)
  const { battery: statusBattery } = useStatusBattery();
  const { battery: rosBattery } = useRobotBattery();

  if (usesRosAbstraction) {
    const isCharging = rosBattery?.power_supply_status === BatteryState_PowerEnum.POWER_SUPPLY_STATUS_CHARGING;
    return {
      battery: {
        percentage: (rosBattery?.percentage ?? 0) * 100,
        isCharging,
        powerStatus: isCharging ? BatteryState_PowerEnum.POWER_SUPPLY_STATUS_CHARGING : BatteryState_PowerEnum.POWER_SUPPLY_STATUS_DISCHARGING,
        voltage: rosBattery && !isNaN(rosBattery.voltage) && rosBattery.voltage !== 0 ? rosBattery.voltage : undefined,
        temperature: rosBattery && !isNaN(rosBattery.temperature) && rosBattery.temperature !== 0 ? rosBattery.temperature : undefined,
      },
    };
  }

  const isCharging = statusBattery?.status === Status_Battery_Status.CHARGING;
  return {
    battery: {
      percentage: statusBattery?.chargePercentage ?? 0,
      isCharging,
      powerStatus: isCharging ? BatteryState_PowerEnum.POWER_SUPPLY_STATUS_CHARGING : BatteryState_PowerEnum.POWER_SUPPLY_STATUS_DISCHARGING,
    },
  };
}
